import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import UclSection from "../../components/layout/UclSection";
import HeroBanner from "../../components/layout/HeroBanner";
import ChangeProfileModal from "../../components/modals/ChangeProfileModal";
import PinEntryModal from "../../components/modals/PinEntryModal";
import { useSelector } from "react-redux";
import { fetchGenres, fetchUserBanners, fetchMovieDetail, fetchTrailer, fetchAgeRatings, getImageUrl, fetchUserWatchlist, fetchMovieDetailsBatch, fetchEpisodeDetails, fetchDashboardContent, fetchLiveChannelCategories, fetchLiveChannels, fetchTvStoreCategories, fetchBrandedVodCategories } from "../../redux/content";
import { getClientApiUrl } from "../../config/apiConfig";
import "../../components/styles/homePage.scss";

/**
 * HomePage — Main home screen
 * Figma: Home Screen (Desktop) (24654:4806) — 1920×6659
 *
 * Static implementation matching Figma spec.
 * Dynamic API-driven sections are commented out below.
 */

// Rotating gradients for genre cards
const GENRE_GRADIENTS = [
  "linear-gradient(to right, #eab5b5 7%, #990009 100%)",
  "linear-gradient(to right, #eab5e1 10%, #8a1f8a 100%)",
  "linear-gradient(to right, #91b7f0 10%, #01417c 100%)",
  "linear-gradient(to right, #f0db91 7%, #7c5a01 100%)",
  "linear-gradient(to right, #d4eca1 7%, #287c01 100%)",
];

const HomePage = () => {
  const navigate = useNavigate();
  const banners = useSelector((state) => state.content.banners);
  const bannersLoading = useSelector((state) => state.content.loaders.banners);
  const watchlist = useSelector((state) => state.content.watchlist);
  const channelCategories = useSelector((state) => state.content.channelCategories);
  const channels = useSelector((state) => state.content.channels);
  const tvStoreCategories = useSelector((state) => state.content.tvStoreCategories);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [genreList, setGenreList] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [slidesBuilding, setSlidesBuilding] = useState(true);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingProfile, setPendingProfile] = useState(null);
  const [cwItems, setCwItems] = useState([]);
  const [dashboardItems, setDashboardItems] = useState([]);
  const [brandedCategories, setBrandedCategories] = useState([]);
  const cwFetchedRef = useRef(false);
  const dashFetchedRef = useRef(false);
  const chanCatsFetchedRef = useRef(false);
  const brandsFetchedRef = useRef(false);
  const tvodMapRef = useRef(new Map());

  // Fetch banners + genres + TV Store categories on mount
  useEffect(() => {
    if (banners.length === 0) fetchUserBanners("DESKTOP");
    if (tvStoreCategories.length === 0) fetchTvStoreCategories();
  }, [banners.length, tvStoreCategories.length]);

  // Fetch watchlist on mount for Continue Watching section (guarded against StrictMode double-fire)
  useEffect(() => {
    if (!cwFetchedRef.current) {
      cwFetchedRef.current = true;
      fetchUserWatchlist();
    }
  }, []);

  // Fetch dashboard VOD content for "New episode" section (CRM dashboard_categories)
  useEffect(() => {
    if (!dashFetchedRef.current) {
      dashFetchedRef.current = true;
      fetchDashboardContent().then((groups) => {
        console.log("[HomePage] Dashboard groups:", groups.length, groups.map(g => `${g.categoryName}(${g.items.length})`).join(", "));
        setDashboardItems(groups);
      });
    }
  }, []);

  // Fetch channel categories + enriched channels for "News Report" section
  useEffect(() => {
    if (!chanCatsFetchedRef.current) {
      chanCatsFetchedRef.current = true;
      fetchLiveChannelCategories();
      if (channels.length === 0) fetchLiveChannels();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch VOD categories flagged `branded: true` for the Brands rail
  useEffect(() => {
    if (!brandsFetchedRef.current) {
      brandsFetchedRef.current = true;
      fetchBrandedVodCategories().then((cats) => {
        console.log("[HomePage] Branded VOD categories:", cats.length, cats.map((c) => c.uid).join(", "));
        setBrandedCategories(cats);
      });
    }
  }, []);

  // Build TVOD product map (for buildDetailNavState). Runs on tvStoreCategories change
  // independently of watchlist — card clicks need it even when the user has no bookmarks.
  useEffect(() => {
    const tvodCategory = tvStoreCategories.find((c) => c.uid === "tvod");
    const tvodProductIds = new Set(tvodCategory?.tv_store?.map((p) => p.id) || []);
    if (tvodProductIds.size === 0) return;
    const loadTvodMap = async () => {
      try {
        const accessToken = localStorage.getItem("mango_access_token");
        const userId = localStorage.getItem("mango_user_id");
        const res = await fetch(`${getClientApiUrl()}/api/client/v1/9mobile/users/${userId}/tv_store`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json();
        if (data.status === "ok") {
          const tvodByUid = new Map();
          data.data?.forEach((p) => { if (tvodProductIds.has(p.id)) tvodByUid.set(p.uid, p); });
          tvodMapRef.current = tvodByUid;
          console.log("[HomePage] TVOD products resolved (standalone):", tvodByUid.size);
        }
      } catch (e) {
        console.warn("[HomePage] Failed to fetch tv_store for TVOD UIDs:", e.message);
      }
    };
    loadTvodMap();
  }, [tvStoreCategories]);

  // When watchlist updates, resolve images for Continue Watching cards
  useEffect(() => {
    if (watchlist.length === 0) {
      setCwItems([]);
      return;
    }

    const loadCwDetails = async () => {
      // Reuse the shared TVOD map populated by the effect above
      const tvodByUid = tvodMapRef.current;

      const movieItems = watchlist.filter((item) => item._type === "movie");
      const episodeItems = watchlist.filter((item) => item._type === "episode");

      console.log("[HomePage] CW resolving — movies:", movieItems.length, "episodes:", episodeItems.length);

      const [movieDetails, episodeDetailMap] = await Promise.all([
        movieItems.length > 0
          ? fetchMovieDetailsBatch(movieItems.map((b) => ({ id: b.movie_id })))
          : Promise.resolve([]),
        episodeItems.length > 0
          ? fetchEpisodeDetails(episodeItems.map((b) => b.episode_id))
          : Promise.resolve({}),
      ]);

      const items = [];

      movieItems.forEach((bookmark) => {
        const detail = movieDetails.find((m) => m.id === bookmark.movie_id);
        const imageUrl = detail
          ? getImageUrl(detail, "BANNER") || getImageUrl(detail, "POSTER")
          : null;
        // uid for player route (fetchVideoUrl expects UID), numericId for details + nav state
        const uid = detail?.uid || bookmark.movie_id;
        // Determine content type: TVOD (Rent) vs SVOD (Discover)
        // Match movie package UIDs against tvod TV Store product UIDs
        const matchedPkgUid = detail?.packages?.find((pkg) => tvodByUid.has(pkg.uid))?.uid;
        const tvodProduct = matchedPkgUid ? tvodByUid.get(matchedPkgUid) : null;
        const isTvod = !!tvodProduct;
        // Per-content purchase status: TVOD checks product purchase, SVOD checks movie.purchased
        const purchased = isTvod
          ? (Array.isArray(tvodProduct?.action) && tvodProduct.action.includes("DEACTIVATE")) ||
            !!tvodProduct?.price_options?.some((po) => po.purchased)
          : !!detail?.purchased;
        items.push({
          id: `movie-${bookmark.movie_id}`,
          uid,
          numericId: bookmark.movie_id,
          imageUrl,
          percentWatched: bookmark.percent_watched || 0,
          purchased,
          fromTab: isTvod ? "Rent" : "Discover",
          // Rental data for MovieDetailsPage (mirrors GenreCategoryPage nav state)
          rentalPrice: tvodProduct?.price_options?.[0]?.activation_price ?? null,
          tvStoreProductId: tvodProduct?.id ?? null,
          rentalPurchased: purchased,
        });
      });

      episodeItems.forEach((bookmark) => {
        const detail = episodeDetailMap[bookmark.episode_id];
        const imageUrl = detail?.images
          ? getImageUrl({ image_id: detail.images.PREVIEW || detail.images.POSTER })
          : null;
        const seriesId = detail?.series_id || bookmark.episode_id;
        items.push({
          id: `episode-${bookmark.episode_id}`,
          uid: seriesId,
          numericId: seriesId,
          imageUrl,
          percentWatched: bookmark.percent_watched || 0,
          purchased: !!detail?.purchased,
          fromTab: "Discover",
        });
      });

      // Only show items the user has actually started watching (>=1% progress)
      const startedItems = items.filter((item) => item.percentWatched >= 1);
      console.log("[HomePage] CW items ready:", startedItems.length, "(filtered from", items.length, "bookmarks)");
      setCwItems(startedItems);
    };

    loadCwDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlist]);

  // Build hero slides from VOD banners (same as DiscoveryPage)
  const vodBanners = banners.filter((b) => b.type === "VOD");

  useEffect(() => {
    if (vodBanners.length === 0) return;

    const loadSlides = async () => {
      console.log("[HomePage] Building hero slides for", vodBanners.length, "VOD banners");
      const [genreMap, ageRatingMap] = await Promise.all([fetchGenres(), fetchAgeRatings()]);

      const slides = await Promise.all(
        vodBanners.map(async (banner) => {
          const slide = {
            bgImage: banner.preview_image_id ? getImageUrl({ image_id: banner.preview_image_id }) : null,
            title: banner.title || null,
            description: banner.description || null,
            playHref: banner.content_id ? `/player/${banner.link || banner.content_id}?type=movie` : null,
            // VideoPlayerPage uses movieId (numeric) for the /movies/{id} detail
            // endpoint — UID alone returns 502. Pass content_id through state.
            playState: banner.content_id ? { movieId: banner.content_id } : null,
            detailsHref: banner.content_id ? `/details/${banner.content_id}` : null,
            trailerUrl: null, rating: null, rated: null, year: null, genres: null,
          };

          if (banner.content_id) {
            try {
              const movie = await fetchMovieDetail(banner.content_id);
              if (movie) {
                slide.rating = movie.critics_rating ? String(movie.critics_rating) : null;
                slide.year = movie.year ? String(movie.year) : null;
                slide.rated = movie.age_rating_id ? ageRatingMap[movie.age_rating_id] || null : null;
                const genreIds = movie.movie_genres || movie.genres || [];
                slide.genres = genreIds.length > 0 ? genreIds.map((g) => genreMap[g.id || g] || "").filter(Boolean) : null;
                if (!slide.description || slide.description.length < 30) slide.description = movie.description || slide.description;
                const titleArtStoreId = Array.isArray(movie.movie_image_stores)
                  ? movie.movie_image_stores.find((s) => s.type === "TITLE_ART")?.image_store_id
                  : null;
                const titleArtId = movie.title_art_image_id || titleArtStoreId;
                if (titleArtId) {
                  slide.titleArt = getImageUrl({ image_id: titleArtId });
                }
                if (movie.cm_trailer || movie.trailer_url_ott || movie.has_trailer) {
                  const trailerUrl = await fetchTrailer(banner.content_id, "movie");
                  if (trailerUrl) slide.trailerUrl = trailerUrl;
                }
              }
            } catch (e) {
              console.warn("[HomePage] Failed to load movie meta for banner:", banner.title, e.message);
            }
          }
          return slide;
        })
      );

      console.log("[HomePage] Hero slides built:", slides.map((s) => s.title).join(", "));
      setHeroSlides(slides);
      setSlidesBuilding(false);
    };

    loadSlides();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vodBanners.length]);

  // Fetch genres for the genres section
  useEffect(() => {
    const loadGenres = async () => {
      const genreMap = await fetchGenres();
      if (genreMap && Object.keys(genreMap).length > 0) {
        const genres = Object.entries(genreMap).map(([id, name]) => ({ id: Number(id), name }));
        setGenreList(genres);
        console.log("[HomePage] Genres loaded:", genres.length, genres.slice(0, 5).map(g => g.name).join(", "));
      }
    };
    loadGenres();
  }, []);

  const handleProfileSelect = () => {
    setShowProfileModal(false);
  };

  const handlePinRequired = (profile) => {
    setPendingProfile(profile);
    setShowProfileModal(false);
    setShowPinModal(true);
  };

  const handlePinSubmit = () => {
    setShowPinModal(false);
    handleProfileSelect(pendingProfile);
  };

  // Build navigate state for a movie item — detects TVOD for rental price + active tab
  const buildDetailNavState = (item) => {
    const tvodByUid = tvodMapRef.current;
    // Try package UID match first (movie details have packages), then fall back to
    // movie's own uid — category list endpoint omits packages, but TVOD product UIDs
    // equal the movie UID for rental content (e.g. "theescort", "bonnyclara").
    const matchedPkgUid = item.packages?.find((pkg) => tvodByUid.has(pkg.uid))?.uid;
    const tvodProduct = matchedPkgUid
      ? tvodByUid.get(matchedPkgUid)
      : (item.uid && tvodByUid.has(item.uid) ? tvodByUid.get(item.uid) : null);
    if (tvodProduct) {
      return {
        contentType: item.type,
        fromTab: "Rent",
        fromRent: true,
        rentalPrice: tvodProduct.price_options?.[0]?.activation_price ?? null,
        tvStoreProductId: tvodProduct.id,
        rentalPurchased:
          (Array.isArray(tvodProduct.action) && tvodProduct.action.includes("DEACTIVATE")) ||
          !!tvodProduct.price_options?.some((po) => po.purchased),
      };
    }
    return { contentType: item.type };
  };

  // Derive sports channels for UclSection from the "sports" channel category
  const sportsCategory = channelCategories.find(
    (c) => (c.uid || "").toLowerCase() === "sports"
  );
  const sportsChannelIds = new Set((sportsCategory?.channels || []).map((c) => c.id));
  const sportsChannels = channels.filter((c) => sportsChannelIds.has(c.id));

  return (
    <div className="home-page">
      <Navbar
        activeTab="Home"
        onAvatarClick={() => setShowProfileModal(true)}
      />

      {/* Hero Banner — shared component (Figma 24654:4927) */}
      <HeroBanner
        loading={(bannersLoading || slidesBuilding) && heroSlides.length === 0}
        banners={heroSlides.length > 0 ? heroSlides : undefined}
      />

      {/* OLD DYNAMIC HERO — commented out for Figma spec implementation
      <section className="home-page__hero">
        ... old dynamic hero code ...
      </section>
      */}

      {/* Content Sections */}
      <div className="home-page__content">
        {/* API-driven sections — commented out for Figma spec layout
        {watchlist && watchlist.length > 0 && (
          <ContentRow title="Continue watching" action={<span className="home-page__see-all" onClick={() => navigate("/watchlist")} style={{ cursor: "pointer", color: "#a3a3a3", fontSize: "14px" }}>See all</span>}>
            {watchlist.slice(0, 10).map((item, idx) => (
              <ShowCard key={item.id || idx} title={safeStr(item.title)} image={getImageUrl(item, "BANNER") || getImageUrl(item, "POSTER")} subtitle={safeStr(item.year) || safeStr(item.parental_rating)} variant="landscape" progress={item.progress || item.watch_progress || undefined} onClick={() => handleContentClick(item)} />
            ))}
          </ContentRow>
        )}
        {!loaders.content && categoriesWithContent.map((cat) => {
          const key = cat.uid.toLowerCase();
          const items = moviesByCategories[key] || [];
          if (items.length === 0) return null;
          return (
            <ContentRow key={cat.id || cat.uid} title={formatCategoryName(cat)} action={<span className="home-page__see-all" onClick={() => navigate(`/category/${encodeURIComponent(cat.uid)}`)} style={{ cursor: "pointer", color: "#a3a3a3", fontSize: "14px" }}>See all</span>}>
              {items.slice(0, 10).map((movie, idx) => (
                <ShowCard key={movie.id || idx} title={safeStr(movie.title)} image={getImageUrl(movie, "POSTER")} subtitle={safeStr(movie.year) || safeStr(movie.parental_rating)} variant="poster" onClick={() => handleContentClick(movie)} />
              ))}
            </ContentRow>
          );
        })}
        */}

        {/* ===== Figma spec section order (24654:4806) ===== */}

        {/* 1. Dashboard rows — heading comes from the VOD category name
            (dashboard_categories IDs resolved to names like "New"). */}
        {[0, 1].map((slotIdx) =>
          dashboardItems[slotIdx]?.items?.length > 0 && (
            <section key={`new-${slotIdx}`} className="home-page__new-episode">
              <h2 className="home-page__section-heading">
                {dashboardItems[slotIdx].categoryName || "New"}
              </h2>
              <div className="home-page__new-episode-row">
                {dashboardItems[slotIdx].items.slice(0, 10).map((item, i) => {
                  // Landscape cards: prefer preview_image_id from movie details,
                  // fall back to the portrait image_id when preview is null.
                  const img = item.preview_image_id
                    ? getImageUrl({ image_id: item.preview_image_id })
                    : (getImageUrl(item, "POSTER") || getImageUrl(item));
                  return (
                    <div
                      key={item.id || `new-${slotIdx}-${i}`}
                      className="home-page__new-episode-card"
                      onClick={() => navigate(`/details/${item.id}`, { state: buildDetailNavState(item) })}
                      role="button"
                      tabIndex={0}
                    >
                      {img ? (
                        <img src={img} alt={item.title || ""} className="home-page__new-episode-img" />
                      ) : (
                        <div className="home-page__new-episode-img" style={{ background: "#222", display: "flex", alignItems: "center", justifyContent: "center", color: "#888" }}>
                          {item.title || "Untitled"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )
        )}

        {/* 2. UCL Football Matches Section */}
        {/* Temporarily hidden per client */}
        {false && <UclSection sportsChannels={sportsChannels} />}

        {/* 3. Continue Watching Section — only shown when user has bookmarks */}
        {cwItems.length > 0 && (
          <section className="home-page__cw">
            <h2 className="home-page__section-heading">Continue watching</h2>
            <div className="home-page__cw-row">
              {cwItems.map((item) => (
                <div
                  key={item.id}
                  className="home-page__cw-card"
                  onClick={() => {
                    console.log("[HomePage] CW card clicked:", item.uid, "numericId:", item.numericId, "tab:", item.fromTab);
                    navigate(`/details/${item.numericId}`, {
                      state: {
                        fromTab: item.fromTab,
                        fromRent: item.fromTab === "Rent",
                        rentalPrice: item.rentalPrice,
                        tvStoreProductId: item.tvStoreProductId,
                        rentalPurchased: item.rentalPurchased,
                      },
                    });
                  }}
                >
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt=""
                      className="home-page__cw-card-bg"
                    />
                  )}
                  <div className="home-page__cw-card-gradient" />
                  <div className="home-page__cw-card-actions">
                    <button
                      className="home-page__cw-btn home-page__cw-btn--primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.purchased) {
                          console.log("[HomePage] CW Continue play → player (purchased):", item.uid);
                          navigate(`/player/${item.uid}?type=movie`, { state: { movieId: item.numericId, isPurchased: true } });
                        } else {
                          console.log("[HomePage] CW Continue play → details (not purchased):", item.numericId);
                          navigate(`/details/${item.numericId}`, {
                            state: {
                              fromTab: item.fromTab,
                              fromRent: item.fromTab === "Rent",
                              rentalPrice: item.rentalPrice,
                              tvStoreProductId: item.tvStoreProductId,
                              rentalPurchased: item.rentalPurchased,
                            },
                          });
                        }
                      }}
                    >
                      Continue play
                    </button>
                    <button
                      className="home-page__cw-btn home-page__cw-btn--secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/details/${item.numericId}`, {
                          state: {
                            fromTab: item.fromTab,
                            fromRent: item.fromTab === "Rent",
                            rentalPrice: item.rentalPrice,
                            tvStoreProductId: item.tvStoreProductId,
                            rentalPurchased: item.rentalPurchased,
                          },
                        });
                      }}
                    >
                      See details
                    </button>
                  </div>
                  <div className="home-page__cw-card-progress">
                    <div
                      className="home-page__cw-card-progress-fill"
                      style={{ width: `${item.percentWatched}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 4. Dashboard rows (slots 2-3) — heading from the VOD category name. */}
        {[2, 3].map((slotIdx) =>
          dashboardItems[slotIdx]?.items?.length > 0 && (
            <section key={`comedy-${slotIdx}`} className="home-page__poster-section">
              <h2 className="home-page__section-heading">
                {dashboardItems[slotIdx].categoryName || "Featured"}
              </h2>
              <div className="home-page__poster-row">
                {dashboardItems[slotIdx].items.slice(0, 10).map((item, i) => {
                  const img = getImageUrl(item, "POSTER") || getImageUrl(item);
                  return (
                    <div
                      key={item.id || `comedy-${slotIdx}-${i}`}
                      className="home-page__poster-card"
                      onClick={() => navigate(`/details/${item.id}`, { state: buildDetailNavState(item) })}
                      role="button"
                      tabIndex={0}
                    >
                      {img ? (
                        <img src={img} alt={item.title || ""} className="home-page__poster-img" />
                      ) : (
                        <div className="home-page__poster-img" style={{ background: "#222", display: "flex", alignItems: "center", justifyContent: "center", color: "#888" }}>
                          {item.title || "Untitled"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )
        )}

        {/* Brands Section — VOD categories flagged branded:true (Figma Issue #8) */}
        {/* Temporarily hidden per client */}
        {false && brandedCategories.length > 0 && (
          <section className="home-page__brands">
            <h2 className="home-page__section-heading">Brands</h2>
            <div className="home-page__brands-row">
              {brandedCategories.map((cat) => {
                const headingImg = cat.heading_image_id
                  ? getImageUrl({ image_id: cat.heading_image_id })
                  : null;
                const bg = cat.background_color || "#000000";
                return (
                  <div
                    key={cat.id}
                    className="home-page__brands-card"
                    style={{ backgroundColor: bg, cursor: "pointer" }}
                    onClick={() => navigate(`/brand/${encodeURIComponent(cat.uid)}`)}
                    role="button"
                    tabIndex={0}
                  >
                    {headingImg && (
                      <img
                        src={headingImg}
                        alt={cat.name || cat.uid}
                        className="home-page__brands-logo"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Genres Section (Figma Issue #9) — real genres from API */}
        {/* Temporarily hidden per client */}
        {false && (
        <section className="home-page__genres">
          <h2 className="home-page__section-heading">Genres</h2>
          <div className="home-page__genres-row">
            {(genreList.length > 0 ? genreList : [
              { name: "Action" }, { name: "Romance" }, { name: "Animation" }, { name: "Drama" }, { name: "Comedy" },
            ]).map((genre, i) => (
              <div
                key={genre.id || i}
                className="home-page__genres-card"
                style={{ background: GENRE_GRADIENTS[i % GENRE_GRADIENTS.length] }}
                onClick={() => navigate(`/rent/${encodeURIComponent(genre.name.toLowerCase())}`)}
              >
                <span className="home-page__genres-label">{genre.name}</span>
              </div>
            ))}
          </div>
        </section>
        )}

        {/* News Report Section — enriched channels from the "news" category (Figma Issue #10) */}
        {(() => {
          const newsCat = channelCategories.find(
            (c) => (c.uid || "").toLowerCase() === "news"
          );
          const newsIds = new Set((newsCat?.channels || []).map((c) => c.id));
          // /categories/channels returns only {id, packages}; full data (name, logos, uid) comes from v2 channels.
          const newsChannels = channels.filter((c) => newsIds.has(c.id));
          if (newsChannels.length === 0) return null;
          return (
            <section className="home-page__news">
              <h2 className="home-page__section-heading home-page__section-heading--lg">News Report</h2>
              <div className="home-page__news-row">
                {newsChannels.map((ch, i) => {
                  const logoId =
                    ch.logos?.NORMAL || ch.logos?.CARD ||
                    (ch.image_stores?.find((s) => s.type === "NORMAL") || ch.image_stores?.[0])?.image_id;
                  const img = logoId ? getImageUrl({ image_id: logoId }) : "/assets/news/news-reporter.webp";
                  const title = ch.name || ch.short_name || "Live Channel";
                  return (
                    <div
                      key={ch.id || i}
                      className="home-page__news-card"
                      onClick={() => navigate(`/live/${ch.uid || ch.id}`)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="home-page__news-card-thumb">
                        <img src={img} alt={title} className="home-page__news-card-img" />
                        <img src="/assets/ucl/play-circle.svg" alt="Play" className="home-page__news-card-play" />
                      </div>
                      <div className="home-page__news-card-info">
                        <span className="home-page__news-card-source">{ch.short_name || title}</span>
                        <h3 className="home-page__news-card-title">{title}</h3>
                        <span className="home-page__news-card-live">
                          <span className="home-page__news-card-live-dot" />
                          LIVE
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })()}

        {/* Live TV Section — commented out per Figma spec (not in home screen design)
        {channels.length > 0 && heroChannel && (
          <section className="home-page__live-section">
            <div className="home-page__live-hero" onClick={() => navigate(`/live/${heroChannel.uid || heroChannel.id}`)} role="button" tabIndex={0}>
              <div className="home-page__live-hero-bg">
                {getImageUrl(heroChannel) && <img src={getImageUrl(heroChannel)} alt={safeStr(heroChannel.title || heroChannel.name)} className="home-page__live-hero-img" />}
              </div>
              <div className="home-page__live-hero-overlay" />
              <div className="home-page__live-hero-content">
                <div className="home-page__live-hero-logo">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="18" stroke="white" strokeWidth="1.5" /><circle cx="24" cy="24" r="12" stroke="white" strokeWidth="1.5" /><circle cx="24" cy="24" r="6" fill="white" /></svg>
                  <div className="home-page__live-hero-logo-text">
                    <span>{sportsCategory ? sportsCategory.name?.toUpperCase() : "LIVE"}</span>
                    <span>{sportsCategory ? "TV" : "TV"}</span>
                  </div>
                </div>
                <p className="home-page__live-hero-description">
                  {sportsCategory ? safeStr(sportsCategory.description) || "Watch live sports events streamed in real time on OpenPlay" : "Watch live channels and sports events streamed in real time on OpenPlay"}
                </p>
              </div>
            </div>
            <div className="home-page__live-row">
              <div className="home-page__live-scroll" ref={liveScrollRef}>
                {channels.map((channel, i) => (
                  <div key={channel.id || i} className="home-page__live-card" onClick={() => navigate(`/live/${channel.uid || channel.id}`)} role="button" tabIndex={0}>
                    <div className="home-page__live-card-thumb">
                      {getImageUrl(channel) && <img src={getImageUrl(channel)} alt={safeStr(channel.title || channel.name)} className="home-page__live-card-img" />}
                      <div className="home-page__live-card-play"><svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="11" fill="rgba(0,0,0,0.5)" stroke="white" strokeWidth="1.5" /><path d="M10 8l6 4-6 4V8z" fill="white" /></svg></div>
                    </div>
                    <div className="home-page__live-card-info">
                      <span className="home-page__live-card-category">{getChannelCategoryName(channel.id)}</span>
                      <span className="home-page__live-card-title">{safeStr(channel.title || channel.name)}</span>
                      <span className="home-page__live-card-badge"><span className="home-page__live-dot" />LIVE</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
        */}

        <Footer />
      </div>

      {/* Modals */}
      <ChangeProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onSelectProfile={handleProfileSelect}
        onPinRequired={handlePinRequired}
      />
      <PinEntryModal
        isOpen={showPinModal}
        onClose={() => setShowPinModal(false)}
        onSubmit={handlePinSubmit}
      />
    </div>
  );
};

export default HomePage;
